import { batchGetTrials } from '@/app/apis';
import { useStore } from '@/app/providers/store';
import type { Trials } from '../stores/slices/task-form';
import { TRIAL_GROUP_MAP, type TrialGroupKey } from './trial-groups';
import { useFetch } from './use-fetch';

/**
 * 自定义 Hook，用于更新用户的试用次数信息。
 * 该 Hook 封装了调用后端 API 获取试用次数的逻辑，并在成功获取后
 * 将数据（总次数和剩余次数）更新到 Zustand 全局状态存储中。
 *
 * 调用时机：
 * 1. 在 ClientInit 组件中，在用户初始登录或页面加载时调用
 * 2. 在表单提交按钮中，当用户成功提交创建视频任务且是有限试用场景时
 * 3. 在任务状态处理中，当任务状态变为 "failed" 或 "canceled" 时
 * 4. 在表单设置检查中，在检查用户设置是否有效时，确保获得最新试用次数
 *
 * @returns 返回一个包含手动触发更新函数的对象:
 *   - updateTrials: 触发异步获取和更新试用次数的函数。
 *   - updateTrialsAsync: 与 updateTrials 功能相同，但返回一个 Promise，允许调用者等待更新完成。
 */
export const useTrials = () => {
  const { updateTrialsAll } = useStore((state) => ({
    updateTrialsAll: state.updateTrialsAll,
  }));

  const { run: updateTrials, runAsync: updateTrialsAsync } = useFetch(batchGetTrials, {
    manual: true,
    onSuccess: ({ data }) => {
      const trials = data?.trials;
      if (Array.isArray(trials)) {
        const aggregated: Trials = {
          trials720p: { total: 0, remaining: 0 },
          trialsQ1: { total: 0, remaining: 0 },
          trialsText2Audio: { total: 0, remaining: 0 },
          trialsVoice: { total: 0, remaining: 0 },
          trialsProMode: { total: 0, remaining: 0 },
          trialsQ2TransitionSpeed: { total: 0, remaining: 0 },
          trialsQ2TransitionPro: { total: 0, remaining: 0 },
          trialsQ2T2V: { total: 0, remaining: 0 },
          trialsQ2C2V: { total: 0, remaining: 0 },
          trialsQ2T2I: { total: 0, remaining: 0 },
          trialsQ2R2I: { total: 0, remaining: 0 },
          trialsImage25T2IFast: { total: 0, remaining: 0 },
          trialsImage25T2IPro: { total: 0, remaining: 0 },
          trialsImage25R2IFast: { total: 0, remaining: 0 },
          trialsImage25R2IPro: { total: 0, remaining: 0 },
          trialsQ2Extend: { total: 0, remaining: 0 },
          trialsQ2ExtendPro: { total: 0, remaining: 0 },
          trialsQ2Pro: { total: 0, remaining: 0 },
          trialsQ3T2VI2V: { total: 0, remaining: 0 },
          trialsQ3C2V: { total: 0, remaining: 0 },
          trialsQ4_540p: { total: 0, remaining: 0 },
          trialsQ4_720p: { total: 0, remaining: 0 },
          trialsQ4_1080p: { total: 0, remaining: 0 },
          trialsStoryGrid: { total: 0, remaining: 0 },
          trialsOfflineAvatar: { total: 0, remaining: 0 },
        };

        for (const trial of trials) {
          const { total_times: total = 0, remaining_times: remaining = 0, durations } = trial;
          const group = trial.group as TrialGroupKey | undefined;
          if (!group) continue;

          const trialKey = TRIAL_GROUP_MAP[group];
          if (trialKey && trialKey in aggregated) {
            aggregated[trialKey] = { total, remaining, durations };
          }
        }

        // 一次性批量更新（带有默认值兜底）
        updateTrialsAll(aggregated);
      }
    },
  });

  return {
    updateTrials,
    updateTrialsAsync,
  };
};
